import type { Directive, DirectiveBinding } from 'vue'
import { getCurrencySymbol } from '#src/modules/numberFormat/getCurrencySymbol'
import {
  formatNumber,
  type FormatNumberOptions,
  type NumberFormatStyle
} from '#src/modules/numberFormat/formatNumber'
import { getConfiguredCurrencySymbol } from '#src/services/retailSdkService'

export type NxgRetailFormatNumberCurrencyBinding = {
  value: number | string
  currency: string
}

export type NxgRetailFormatNumberDirectiveValue =
  | number
  | string
  | NxgRetailFormatNumberCurrencyBinding
  | null
  | undefined

const styleFromArg = (arg: string | undefined): NumberFormatStyle => {
  return (arg === 'currency' || arg === 'percent') ? arg : 'decimal'
}

const isObjectBinding = (
  value: Exclude<NxgRetailFormatNumberDirectiveValue, null | undefined>
): value is NxgRetailFormatNumberCurrencyBinding =>
  typeof value === 'object' && 'value' in value

const resolveFormatOptions = (
  binding: DirectiveBinding<NxgRetailFormatNumberDirectiveValue>
): { amount: number | string; options?: FormatNumberOptions } | null => {
  const { value } = binding

  if (value === null || value === undefined) {
    return null
  }

  if (isObjectBinding(value)) {
    if (value.currency) {
      return {
        amount: value.value,
        options: {
          style: 'currency',
          currencySymbol: getCurrencySymbol(value.currency)
        }
      }
    }

    return { amount: value.value, options: { style: 'decimal' } }
  }

  const style = styleFromArg(binding.arg)
  const options: FormatNumberOptions = { style }

  if (style === 'currency') {
    options.currencySymbol = getConfiguredCurrencySymbol()
  }

  return { amount: value, options }
}

const applyNxgRetailFormatNumber = (
  el: HTMLElement,
  binding: DirectiveBinding<NxgRetailFormatNumberDirectiveValue>
): void => {
  const resolved = resolveFormatOptions(binding)

  if (!resolved) {
    el.textContent = ''
    return
  }

  el.textContent = formatNumber(resolved.amount, resolved.options)
}

export const vNxgRetailFormatNumber: Directive<
  HTMLElement,
  NxgRetailFormatNumberDirectiveValue,
  'currency' | 'percent'
> = {
  mounted(el, binding) {
    applyNxgRetailFormatNumber(el, binding)
  },
  updated(el, binding) {
    applyNxgRetailFormatNumber(el, binding)
  }
}
